// Libs
import { useEffect, useState, useRef, useCallback } from "react";
import { format } from "date-fns";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { detectIosSafari } from "@/lib/utils";
import { formatMunicipalityLabel } from "@/lib/municipalityLabel";
import {
  nextMessageDocumentLabel,
  researchChatPreviewDocumentIds,
  shouldPromptToChooseResearchDocuments,
  type PickerDocument,
} from "@/lib/defaultSelectedDocumentIds";

// Web Speech API type declarations
declare global {
  interface Window {
    SpeechRecognition: typeof SpeechRecognition;
    webkitSpeechRecognition: typeof SpeechRecognition;
  }
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  abort(): void;
  addEventListener(type: 'result', listener: (event: SpeechRecognitionEvent) => void): void;
  addEventListener(type: 'end', listener: () => void): void;
  addEventListener(type: 'error', listener: (event: SpeechRecognitionErrorEvent) => void): void;
}

interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
}

interface SpeechRecognitionResultList {
  length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionResult {
  length: number;
  item(index: number): SpeechRecognitionAlternative;
  [index: number]: SpeechRecognitionAlternative;
  isFinal: boolean;
}

interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

declare var SpeechRecognition: {
  prototype: SpeechRecognition;
  new(): SpeechRecognition;
};

// Hooks
import { useChatContext } from "@/context/ChatContext";
import { useMunicipalityContext } from "@/context/MunicipalityContext";
import { useToast } from "@/hooks/useToast";
import { fetchChatConfiguration, getDocumentsForMunicipality, getResearchDocumentDefault } from "@/api/messages";

// Global declarations
declare const Honeybadger: any;

// Components
import { BotMessage, BotWelcomeMessage } from "./BotMessage";
import { ANSWER_REASONING_LABEL } from "./AnswerReasoningPanel";
import { countCitedSourcesWithBody } from "@/lib/citationMarkers";
import type { AddressChatRedirectMeta } from "@/types/chat";
import NewChatDialog from "./NewChatDialog";
import AiDisclaimer from "@/components/AiDisclaimer";
import { consumeLaunchPadOpenNewChat } from "@/lib/launchPadIntent";
import TranslationDialog from "./TranslationDialog";
import { Button } from "@/components/ui/button";
import { EmptyState } from "./EmptyState";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { UserMessage } from "./UserMessage";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "../ui/card";
import { AddressSpecificChatDetails } from "./AddressSpecificChatDetails";
import { MainLoadingState, TaskListLoadingState, type ResearchSummary } from "./LoadingStates";

// UI
import {
  Loader2,
  MessageSquarePlus,
  GraduationCap,
  BookCheck,
  Pencil,
  Send,
  FlaskConical,
  Printer,
  FileText,
  Mic,
  MicOff,
  Languages,
  ScrollText,
} from "lucide-react";
import { DocumentSelector } from "./DocumentSelector";
import { Sheet } from "../ui/sheet";

type MunicipalityLikeContext = {
  municipalities: any[];
  getMunicipality: (id: number) => any | undefined;
  setMunicipalityId: (id: number) => void;
  isLoading?: boolean;
};
type UseMunicipalityLikeContext = () => MunicipalityLikeContext;

interface ChatProps {
  useMunicipality?: UseMunicipalityLikeContext;
}

interface ChatMessage {
  id: number;
  content: string;
  source: "from_human" | "from_bot";
  created_at: string;
  updated_at: string;
  meta_data: any;
  selected_document_ids: number[];
  chat_id: number;
}

interface ImprovedPrompt {
  messageId: string;
  prompt: string;
}

export function Chat({ useMunicipality = useMunicipalityContext }: ChatProps) {
  const {
    getCurrentConversation,
    setCurrentConversationForKind,
    updateTitle,
    isLoading,
    createChat,
    isCreatingChat,
    sendPrompt,
    isStreamingResponse,
    progressMessage,
  } = useChatContext();
  const currentConversation = getCurrentConversation("research_assistant");
  const { getMunicipality, municipalities, setMunicipalityId, isLoading: municipalitiesLoading } = useMunicipality();
  const municipality = currentConversation ? getMunicipality(currentConversation.municipality_id) : undefined;
  const addressLookupProvider = municipality?.address_lookup?.provider ?? currentConversation?.address_lookup_provider ?? undefined;
  const addressLookupProviderLoading =
    !addressLookupProvider && currentConversation?.address_lookup_provider === undefined && Boolean(municipalitiesLoading);
  const [isEditing, setIsEditing] = useState(false);
  const [showNewChatDialog, setShowNewChatDialog] = useState(false);

  useEffect(() => {
    const intent = consumeLaunchPadOpenNewChat();
    if (intent === "research_assistant") {
      setShowNewChatDialog(true);
    }
  }, []);

  const [selectedMunicipality, setSelectedMunicipality] = useState<string>("");
  const [addressRedirectLock, setAddressRedirectLock] = useState<AddressChatRedirectMeta | null>(null);

  // Update the current municipality in context whenever conversation changes
  useEffect(() => {
    if (currentConversation?.municipality_id) {
      setMunicipalityId(currentConversation.municipality_id);
    }
  }, [currentConversation?.municipality_id, setMunicipalityId]);

  const { toast } = useToast();
  const messageCount = currentConversation?.messages?.length ?? 0;
  const humanMessageCount = currentConversation?.messages?.filter((m) => m.source === "from_human")?.length ?? 0;
  const ref = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isImprovingPrompt, setIsImprovingPrompt] = useState(false);
  const [suggestingMessageId, setSuggestingMessageId] = useState<string | null>(null);
  const [isSummarizingSources, setIsSummarizingSources] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [showTranslationDialog, setShowTranslationDialog] = useState(false);
  const [messageToTranslate, setMessageToTranslate] = useState<number | null>(null);
  const [improvedPrompts, setImprovedPrompts] = useState<ImprovedPrompt[]>([]);
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<number[]>([]);
  const [selectionSnapshot, setSelectionSnapshot] = useState<{
    chatId: number;
    availableCount: number;
  } | null>(null);
  const [totalDocumentCount, setTotalDocumentCount] = useState<number>(0);
  const [isListening, setIsListening] = useState(false);
  const [recognition, setRecognition] = useState<SpeechRecognition | null>(null);
  const lastProcessedResultIndexRef = useRef(0);
  // Detect iOS Safari to work around 100vh/address bar height issues
  const isIosSafari = detectIosSafari();
  const [streamingStartTime, setStreamingStartTime] = useState<number>(0);
  const [lastResearchSummary, setLastResearchSummary] = useState<ResearchSummary | null>(null);
  const researchSummaryRef = useRef<ResearchSummary | null>(null);
  const wasStreamingRef = useRef<boolean>(false);
  const openAnswerReasoningForLastBotRef = useRef<(() => Promise<void>) | null>(null);
  const registerOpenAnswerReasoning = useCallback((open: (() => Promise<void>) | null) => {
    openAnswerReasoningForLastBotRef.current = open;
  }, []);

  // Fetch chat configuration when conversation changes
  useEffect(() => {
    let cancelled = false;
    const fetchConfiguration = async () => {
      const conversationId = currentConversation?.id;
      setSelectionSnapshot(null);
      const publishSelection = (chatId: number, selectedIds: number[], availableCount: number) => {
        if (cancelled) return;
        setSelectedDocumentIds(selectedIds);
        setSelectionSnapshot({ chatId, availableCount });
      };

      if (conversationId && conversationId !== -1) {
        setIsEditing(false);

        try {
          // Fetch the latest configuration from the backend
          console.log(`Fetching configuration for chat ${conversationId}`);
          const config = await fetchChatConfiguration(conversationId);
          if (cancelled) return;

          let configurationOverlay: { currently_selected_documents: number[] } | undefined;

          // If municipality is available, get all completed documents
          if (municipality?.id) {
            const response = await getDocumentsForMunicipality(municipality.id);
            if (cancelled) return;

            const docs: PickerDocument[] = Array.isArray(response) ? response : (response?.documents || []);

            if (docs.length > 0) {
              const completedDocIds = docs.filter((doc) => doc.status === "completed").map((doc) => doc.id);
              const savedIds = config?.currently_selected_documents || [];
              const resolvedSelectedIds = researchChatPreviewDocumentIds(savedIds, completedDocIds);

              publishSelection(conversationId, resolvedSelectedIds, completedDocIds.length);
              configurationOverlay = { currently_selected_documents: resolvedSelectedIds };
            } else {
              // Fallback if we couldn't get valid documents
              publishSelection(conversationId, config?.currently_selected_documents || [], 0);
            }
          } else {
            // No municipality yet, just use whatever is in the config
            setSelectedDocumentIds(config?.currently_selected_documents || []);
          }

          // Update the conversation in context to ensure it has the latest configuration
          // Only do this if we have a valid configuration to avoid overwriting other settings
          if (config) {
            setCurrentConversationForKind("research_assistant", (prev) => {
              if (!prev || prev.id !== conversationId) {
                return prev;
              }
              return {
                ...prev,
                configuration: {
                  ...prev.configuration,
                  ...config,
                  ...configurationOverlay,
                }
              };
            });
          }
        } catch (error) {
          if (cancelled) return;
          console.error("Error fetching chat configuration:", error);
          toast({
            title: "Failed to load document selection",
            description: "Using all documents for now",
            variant: "destructive",
          });
          setSelectedDocumentIds([]);
        }
      } else if (currentConversation) {
        // For brand new conversations, load all available documents for the municipality
        if (municipality?.id) {
          try {
            const response = await getDocumentsForMunicipality(municipality.id);
            if (cancelled) return;
            const docs: PickerDocument[] = Array.isArray(response) ? response : (response?.documents || []);

            if (docs.length > 0) {
              const completedDocIds = docs.filter((doc) => doc.status === "completed").map((doc) => doc.id);
              const saved = await getResearchDocumentDefault(municipality.id);
              if (cancelled) return;
              const defaultSelectedIds = researchChatPreviewDocumentIds(saved.document_ids, completedDocIds);

              console.log(`New conversation: Setting ${defaultSelectedIds.length} documents as selected`);
              publishSelection(currentConversation.id, defaultSelectedIds, completedDocIds.length);

              // Also update the context with the default selection
              setCurrentConversationForKind("research_assistant", (prev) => {
                if (!prev || prev.id !== conversationId) {
                  return prev;
                }
                return {
                  ...prev,
                  configuration: {
                    ...prev.configuration,
                    currently_selected_documents: defaultSelectedIds
                  }
                };
              });
            }
          } catch (error) {
            if (cancelled) return;
            console.error("Error loading documents for new conversation:", error);
            setSelectedDocumentIds([]);
          }
        } else {
          setSelectedDocumentIds([]);
        }
      }
    };

    fetchConfiguration();
    return () => {
      cancelled = true;
    };
  }, [currentConversation?.id, municipality?.id]);

  // Initialize speech recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognitionClass = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SpeechRecognitionClass();
      recognition.continuous = true; // Keep listening until manually stopped
      recognition.interimResults = true; // Show interim results for better UX
      recognition.lang = 'en-US';

      recognition.addEventListener('result', (event: SpeechRecognitionEvent) => {
        let finalTranscript = '';
        let interimTranscript = '';

        // Only process new results since the last processed index
        for (let i = lastProcessedResultIndexRef.current; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
          } else {
            interimTranscript += transcript;
          }
        }

        const textarea = document.querySelector('textarea[name="prompt"]') as HTMLTextAreaElement;
        if (textarea && finalTranscript) {
          // Only append final results to avoid duplicates
          const currentValue = textarea.value;
          const trimmedTranscript = finalTranscript.trim();
          const newValue = currentValue ? `${currentValue} ${trimmedTranscript}` : trimmedTranscript;
          textarea.value = newValue;
          textarea.focus();

          // Update the last processed index to the current length
          lastProcessedResultIndexRef.current = event.results.length;
        }
      });

      recognition.addEventListener('end', () => {
        // Reset the processed index when recognition ends
        lastProcessedResultIndexRef.current = 0;
        // Only set listening to false if we're not manually stopping it
        // This handles cases where recognition stops unexpectedly
        setIsListening(false);
      });

      recognition.addEventListener('error', (event: SpeechRecognitionErrorEvent) => {
        console.error('Speech recognition error:', event.error);
        lastProcessedResultIndexRef.current = 0;
        setIsListening(false);
        toast({
          title: "Speech recognition error",
          description: `Failed to recognize speech: ${event.error}`,
          variant: "destructive",
        });
      });

      setRecognition(recognition);
    }
  }, [toast]);

  // Fetch total document count when municipality changes
  useEffect(() => {
    if (municipality?.id) {
      // Function to load document count
      const loadDocumentCount = async () => {
        try {
          const response = await getDocumentsForMunicipality(municipality.id);
          const docs: PickerDocument[] = Array.isArray(response) ? response : (response?.documents || []);

          if (docs.length > 0) {
            // Only count completed documents
            const completedDocs = docs.filter((doc) => doc.status === "completed");
            console.log(`Municipality ${municipality.id} has ${completedDocs.length} completed documents out of ${docs.length} total`);
            setTotalDocumentCount(completedDocs.length);
          }
        } catch (error) {
          console.error("Error loading document count:", error);
          // Keep existing count if we have one, otherwise reset to 0
          if (totalDocumentCount === 0) {
            setTotalDocumentCount(0);
          }
        }
      };

      loadDocumentCount();
    }
  }, [municipality?.id]);

  useEffect(() => {
    if (isStreamingResponse && ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [isStreamingResponse]);

  // Capture research summary when streaming stops and save to backend
  useEffect(() => {
    if (wasStreamingRef.current && !isStreamingResponse) {
      // Streaming just stopped - capture the research summary from the ref
      const summary = researchSummaryRef.current;
      if (summary && summary.phases.length > 0) {
        setLastResearchSummary(summary);
        
        // Save to backend - find the last bot message
        const messages = currentConversation?.messages || [];
        const lastBotMessage = [...messages].reverse().find(m => m.source === "from_bot");
        
        if (lastBotMessage && currentConversation?.id) {
          // Save research summary to message meta_data
          fetch(`/api/v1/chats/${currentConversation.id}/messages/${lastBotMessage.id}`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "",
            },
            body: JSON.stringify({
              message: {
                research_summary: summary
              }
            })
          }).then(response => {
            if (!response.ok) {
              console.error(`Failed to save research summary: ${response.status} ${response.statusText}`);
            }
          }).catch(error => {
            console.error("Failed to save research summary:", error);
          });
        }
      }
      researchSummaryRef.current = null;
    }
    wasStreamingRef.current = isStreamingResponse;
  }, [isStreamingResponse, currentConversation?.id, currentConversation?.messages]);

  const handleSave = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const title = (form.elements.namedItem("title") as HTMLInputElement).value;

    if (currentConversation?.id) {
      await updateTitle(currentConversation.id, title, "research_assistant");
      setIsEditing(false);
    }
  };

  const handleSendMessage = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isStreamingResponse || isListening) {
      return;
    }
    const form = event.currentTarget;
    const textarea = form.elements.namedItem("prompt") as HTMLTextAreaElement;
    const prompt = textarea.value.trim();

    if (prompt) {
      try {

        // Add the user's message to the current conversation immediately
        if (currentConversation?.id) {
          // Create the message object
          const newMessage: ChatMessage = {
            id: Date.now(), // Temporary ID
            chat_id: currentConversation.id,
            content: prompt,
            source: "from_human",
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            meta_data: null,
            selected_document_ids: selectedDocumentIds,
          };


          const updatedConversation = {
            ...currentConversation,
            messages: [...(currentConversation.messages || []), newMessage],
          };

          setCurrentConversationForKind("research_assistant", updatedConversation);
          form.reset();
        }

        await sendPrompt(prompt, selectedDocumentIds, "research_assistant");
      } catch (error) {
        console.error("Error sending message:", error);
        Honeybadger.notify(error);
        toast({
          title: "Failed to send message",
          description: "Please try again later",
          variant: "destructive",
        });
      }
    }
  };

  const handleNewChat = () => {
    setAddressRedirectLock(null);
    const only = municipalities.length === 1 ? municipalities[0] : null;
    if (only && !only.address_lookup) {
      void handleStartChat(only.id);
      return;
    }

    if (municipalities.length === 1) {
      setSelectedMunicipality(String(municipalities[0].id));
    } else {
      setSelectedMunicipality("");
    }

    setShowNewChatDialog(true);
  };

  const handleAddressChatRedirect = useCallback((meta: AddressChatRedirectMeta) => {
    setSelectedMunicipality(String(meta.municipality_id));
    setAddressRedirectLock(meta);
    setShowNewChatDialog(true);
  }, []);

  const handleNewChatDialogOpenChange = useCallback((open: boolean) => {
    setShowNewChatDialog(open);
    if (!open) {
      setAddressRedirectLock(null);
    }
  }, []);

  const handleStartChat = async (municipalityId: number, addressData?: Record<string, unknown>) => {
    try {
      await createChat(municipalityId, addressData, "research_assistant");
      setShowNewChatDialog(false);
      setSelectedMunicipality("");
      setAddressRedirectLock(null);
    } catch (error) {
      Honeybadger.notify(error);
      toast({ title: "Failed to create chat", description: "Please try again later", variant: "destructive" });
    }
  };

  // Show address details as first message for new chats with address data
  const renderInitialAddressDetails = () => {
    const addressData = currentConversation?.configuration?.address_data ?? {};
    if (Object.keys(addressData).length > 0) {
      return (
        <div className="chat-message from-bot">
          <AddressSpecificChatDetails
            data={addressData}
            provider={addressLookupProvider}
            providerLoading={addressLookupProviderLoading}
          />
        </div>
      );
    }
    return null;
  };

  const prompts = [
    "What is the maximum occupancy for a restaurant in town?",
    "When does an overnight lodging require automatic sprinkler systems?",
    "Do solar panels require a permit in residential zones?",
    "What are the signage regulations for construction sites?",
    "Does a firework vendor need insurance?",
    "When does a new pool require a permit?",
    "Are chickens allowed in residential zones?",
    "When can businesses serve alcohol outdoors?",
    "How are street names determined?",
    "What are the recycling rules in the city?",
    "Are dogs required to be leashed?",
    "What are the library hours?",
    "How do I report a pothole?",
    "Does the city allow heavy machinery to be parked on gravel?",
    "Are ADUs allowed in the code?",
    "How do I apply for a building permit?",
    "When is the pool open?",
    "What are the requirements to run for mayor?",
  ];

  const handleImprovePrompt = async (originalPrompt: string, messageId: number) => {
    setIsImprovingPrompt(true);
    try {
      const response = await fetch('/api/v1/completions/improve_prompt', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "",
        },
        body: JSON.stringify({
          improve_prompt: {
            text: originalPrompt
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to improve prompt');
      }

      const data = await response.json();

      // Store the improved prompt with the message ID
      setImprovedPrompts(prev => [...prev, { messageId: String(messageId), prompt: data.response }]);
    } catch (error) {
      console.error("Error improving prompt:", error);
      toast({
        title: "Failed to improve prompt",
        description: "Please try again later",
        variant: "destructive",
      });
    } finally {
      setIsImprovingPrompt(false);
    }
  };

  const handleSuggestedPrompt = async (text: string, messageId: string) => {
    try {
      await sendPrompt(text, undefined, "research_assistant");
      setImprovedPrompts(prev =>
        prev.filter(p => p.messageId !== messageId)
      );
    } catch (error) {
      console.error("Error sending message:", error);
      toast({
        title: "Failed to send message",
        description: "Please try again later",
        variant: "destructive",
      });
    }
  };

  const handleSuggestQueries = async (messageContent: string, messageId: string) => {
    setSuggestingMessageId(messageId);
    try {
      const response = await fetch('/api/v1/completions/suggest_queries', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "",
        },
        body: JSON.stringify({
          query_suggestion: {
            text: messageContent
          }
        })
      });

      const data = await response.json();
      const queries = data.response.split(';')
        .map((q: string) => q.trim())
        .filter((q: string) => q.length > 0);

      setImprovedPrompts(prev => {
        const newPrompts = queries.map((query: string) => ({
          messageId: String(messageId),
          prompt: query
        }));
        return [...prev, ...newPrompts];
      });
    } catch (error) {
      console.error('Error suggesting queries:', error);
      Honeybadger.notify(error);
      toast({
        title: "Failed to suggest queries",
        description: "Please try again later",
        variant: "destructive",
      });
    } finally {
      setSuggestingMessageId(null);
    }
  };

  const sanitizeLLMOutput = (content: string): string => {
    const patterns = [
      /<abc123>/gi,
      /<\/abc123>/gi,
      /<chunk>/gi,
      /<\/chunk>/gi,
      /\$\$\$/g,
      /\^\^\^/g,
      /```/g,
    ];
    let out = content || "";
    patterns.forEach((pattern) => {
      out = out.replace(pattern, "");
    });
    const trimmed = out.trim();
    if (trimmed.startsWith("```")) {
      out = trimmed.replace(/^```[\w-]*\s*\n?/, "");
      out = out.replace(/\n?```\s*$/, "");
    }
    return out.trim();
  };

  const handlePrintChat = useCallback(() => {
    if (!currentConversation?.messages || currentConversation.messages.length === 0) {
      toast({
        title: "No messages to print",
        description: "There are no messages in this chat to print.",
        variant: "destructive",
      });
      return;
    }

    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast({
        title: "Printing failed",
        description: "Your browser blocked the print window. Please allow popups and try again.",
        variant: "destructive",
      });
      return;
    }

    // Create the HTML content for printing
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Chat Conversation - ${currentConversation.title || 'Untitled'}</title>
          <!-- Include marked.js for markdown parsing -->
          <script src="https://cdn.jsdelivr.net/npm/marked/marked.min.js"></script>
          <style>
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
              line-height: 1.6;
              color: #333;
              max-width: 800px;
              margin: 0 auto;
              padding: 20px;
            }
            .chat-header {
              text-align: center;
              margin-bottom: 30px;
              border-bottom: 1px solid #eee;
              padding-bottom: 20px;
            }
            .message {
              margin-bottom: 20px;
              padding: 15px;
              border-radius: 8px;
            }
            .user-message {
              background-color: #f0f0f0;
              margin-left: 20%;
            }
            .bot-message {
              background-color: #e6f7ff;
              margin-right: 20%;
            }
            .message-header {
              font-weight: bold;
              margin-bottom: 5px;
            }
            .message-content {
              white-space: pre-wrap;
            }
            .message-content p {
              margin-top: 0;
              margin-bottom: 1em;
            }
            .message-content p:last-child {
              margin-bottom: 0;
            }
            .message-content ul, .message-content ol {
              padding-left: 1.5em;
              margin-bottom: 1em;
            }
            .message-content code {
              background-color: #f5f5f5;
              padding: 0.2em 0.4em;
              border-radius: 3px;
              font-family: monospace;
            }
            .message-content pre {
              background-color: #f5f5f5;
              padding: 1em;
              border-radius: 5px;
              overflow-x: auto;
              margin-bottom: 1em;
            }
            .message-content pre code {
              background-color: transparent;
              padding: 0;
            }
            .message-content blockquote {
              border-left: 4px solid #ddd;
              padding-left: 1em;
              margin-left: 0;
              color: #666;
            }
            .message-content table {
              border-collapse: collapse;
              width: 100%;
              margin-bottom: 1em;
            }
            .message-content th, .message-content td {
              border: 1px solid #ddd;
              padding: 8px;
              text-align: left;
            }
            .message-content th {
              background-color: #f5f5f5;
            }
            .message-time {
              font-size: 0.8em;
              color: #666;
              margin-top: 5px;
              text-align: right;
            }
            @media print {
              .no-print {
                display: none;
              }
            }
          </style>
        </head>
        <body>
          <div class="chat-header">
            <h1>${currentConversation.title || 'Untitled Chat'}</h1>
            <p>Printed from <a href="https://www.ordinalforgov.com" target="_blank" rel="noopener noreferrer">Ordinal</a> on ${format(new Date(), 'PPpp')}</p>
          </div>
          <div class="chat-messages">
            ${currentConversation.messages.map(message => `
              <div class="message ${message.source === 'from_human' ? 'user-message' : 'bot-message'}">
                <div class="message-header">${message.source === 'from_human' ? 'You' : 'Ordinal'}</div>
                <div class="message-content">${message.source === 'from_human' ? message.content : ''}</div>
                ${message.source === 'from_bot' ? `<div class="message-content markdown-content" data-markdown="${encodeURIComponent(sanitizeLLMOutput(message.content || ''))}"></div>` : ''}
                <div class="message-time">${format(new Date(message.created_at), 'PPp')}</div>
              </div>
            `).join('')}
          </div>
          <div class="no-print" style="margin-top: 30px; text-align: center;">
            <button onclick="window.print()">Print</button>
          </div>
          <script>
            // Process markdown content after the page loads
            document.addEventListener('DOMContentLoaded', function() {
              // Configure marked options
              marked.setOptions({
                gfm: true,
                breaks: true,
                sanitize: false
              });

              // Process all markdown content
              document.querySelectorAll('.markdown-content').forEach(function(element) {
                const markdown = decodeURIComponent(element.getAttribute('data-markdown') || '');
                element.innerHTML = marked.parse(markdown);
              });
            });
          </script>
        </body>
      </html>
    `;

    // Write the content to the new window
    printWindow.document.open();
    printWindow.document.write(printContent);
    printWindow.document.close();

    // Wait for content to load before printing
    printWindow.onload = function() {
      printWindow.focus();
      // Give a small delay to ensure markdown is processed
      setTimeout(() => {
        printWindow.print();
        // Close the window after printing (optional)
        // printWindow.close();
      }, 500);
    };
  }, [currentConversation, toast]);
  const toggleSpeechRecognition = () => {
    if (!recognition) {
      toast({
        title: "Speech recognition not supported",
        description: "Your browser doesn't support speech recognition. Please use Chrome, Edge, or Safari.",
        variant: "destructive",
      });
      return;
    }

    if (isListening) {
      recognition.stop();
      setIsListening(false);
    } else {
      try {
        // Reset the processed index when starting a new session
        lastProcessedResultIndexRef.current = 0;
        recognition.start();
        setIsListening(true);
      } catch (error) {
        console.error('Failed to start speech recognition:', error);
        toast({
          title: "Failed to start speech recognition",
          description: "Please make sure you have granted microphone permissions.",
          variant: "destructive",
        });
      }
    }
  };

  const handleSummarizeSources = async (messageContent: string, _messageId: string) => {
    setIsSummarizingSources(true);
    try {
      const response = await fetch('/api/v1/completions/summarize_sources', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "",
        },
        // The server saves the summary as the chat's bot message (message
        // source is server-owned, ENG-2246) and returns it.
        body: JSON.stringify({
          chat_id: currentConversation?.id,
          source_summary: {
            text: messageContent
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to summarize sources');
      }

      const data = await response.json();

      if (currentConversation?.id && data.message) {
        const updatedConversation = {
          ...currentConversation,
          messages: [...(currentConversation.messages || []), data.message],
        };

        setCurrentConversationForKind("research_assistant", updatedConversation);
      }
    } catch (error) {
      console.error('Error summarizing sources:', error);
      toast({
        title: "Failed to summarize sources",
        description: "Please try again later",
        variant: "destructive",
      });
    } finally {
      setIsSummarizingSources(false);
    }
  };

  const handleTranslateResponse = async (targetLanguage: string) => {
    if (!messageToTranslate) {
      return;
    }

    setIsTranslating(true);
    try {
      // Call the new translations endpoint that stores the translation
      const response = await fetch(`/api/v1/messages/${messageToTranslate}/translations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "",
        },
        body: JSON.stringify({
          translation: {
            target_language: targetLanguage
          }
        })
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        const isValidationFailure =
          response.status === 422 &&
          errorBody?.error_code === "translation_validation_failed";

        toast({
          title: isValidationFailure
            ? "Translation could not be verified"
            : "Failed to translate response",
          description: isValidationFailure && errorBody?.retried
            ? "We tried twice. Please try again later."
            : "Please try again later",
          variant: "destructive",
        });
        return;
      }

      const translationData = await response.json();

      // Update the specific message in the conversation with the new translation
      if (currentConversation) {
        const updatedMessages = currentConversation.messages?.map((msg) => {
          if (msg.id === messageToTranslate) {
            // Add the new translation to the message's translations array
            const existingTranslations = msg.message_translations || [];
            // Check if translation for this language already exists (could be returned from backend)
            const hasExisting = existingTranslations.some(t => t.target_language === targetLanguage);
            return {
              ...msg,
              message_translations: hasExisting
                ? existingTranslations
                : [...existingTranslations, translationData]
            };
          }
          return msg;
        });

        setCurrentConversationForKind("research_assistant", {
          ...currentConversation,
          messages: updatedMessages,
        });
      }

      setShowTranslationDialog(false);
      setMessageToTranslate(null);
    } catch (error) {
      console.error('Error translating response:', error);
      toast({
        title: "Failed to translate response",
        description: "Please try again later",
        variant: "destructive",
      });
    } finally {
      setIsTranslating(false);
    }
  };

  const openTranslationDialog = (messageId: number) => {
    setMessageToTranslate(messageId);
    setShowTranslationDialog(true);
  };

  const readySnapshot =
    selectionSnapshot && currentConversation && selectionSnapshot.chatId === currentConversation.id
      ? selectionSnapshot
      : null;
  const promptForEmptySelection =
    readySnapshot != null &&
    shouldPromptToChooseResearchDocuments({
      selectedIds: selectedDocumentIds,
      availableCount: readySnapshot.availableCount,
      messageCount: currentConversation?.messages?.length ?? 0,
      chatKind: currentConversation?.kind,
    });

  if (!currentConversation && !isLoading && !isCreatingChat) {
    return (
      <>
        <EmptyState onNewChat={handleNewChat} />
      <NewChatDialog
        open={showNewChatDialog}
        onOpenChange={handleNewChatDialogOpenChange}
        initialMunicipality={selectedMunicipality}
        onMunicipalityChange={setSelectedMunicipality}
        onStartChat={handleStartChat}
        municipalities={municipalities ?? []}
        title={addressRedirectLock?.title}
        description={addressRedirectLock?.description}
        lockedMunicipalityId={addressRedirectLock?.municipality_id}
      />
    </>
    );
  }

  if (isLoading || isCreatingChat) {
    return <MainLoadingState progressMessage={progressMessage} />;
  }

  let isLastMessageHuman = currentConversation?.messages?.slice(-1)[0]?.source === "from_human";

  return (
    <>
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <div
          className="grid grid-rows-[auto_1fr_auto] h-full"
          style={{ height: isIosSafari ? '-webkit-fill-available' : '100%' }}
        >
          <div className="border-b p-4 flex items-center justify-between shrink-0">
            <Button
              variant="outline"
              className="px-2"
              onClick={handleNewChat}
              style={{ marginLeft: "var(--chat-left-margin)" }}
            >
              <MessageSquarePlus className="h-5 w-5" />
              <span className="ml-2 hidden sm:inline">New Chat</span>
            </Button>

            <div className="flex items-center gap-2 h-10">
              {isEditing ? (
                <form onSubmit={handleSave} className="flex items-center gap-2 h-10">
                  <Input
                    defaultValue={currentConversation?.title ?? format(new Date(currentConversation?.created_at || new Date()), "PPp") }
                    className="h-10 w-fit"
                    placeholder="Enter conversation title"
                    name="title"
                    required
                    autoFocus
                  />
                  <div className="flex gap-2 shrink-0">
                    <Button size="sm">Save</Button>
                    <Button size="sm" variant="ghost" onClick={() => setIsEditing(false)}>
                      Cancel
                    </Button>
                  </div>
                </form>
              ) : (
                <>
                  <Input
                    value={currentConversation?.title ?? format(new Date(currentConversation?.created_at || new Date()), "PPp") }
                    className="h-10 w-fit max-w-[100px] md:max-w-[150px] lg:max-w-[300px] !p-0 !m-0 !border-0 !shadow-none !bg-transparent !outline-none !ring-0 hover:cursor-pointer"
                    title={currentConversation?.title ?? format(new Date(currentConversation?.created_at || new Date()), "PPp") }
                    placeholder="Enter conversation title"
                    readOnly
                    onClick={() => setIsEditing(true)}
                    onFocus={() => setIsEditing(true)}
                    suppressHydrationWarning
                  />
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="ghost" size="icon" onClick={() => setIsEditing(true)} className="h-8 w-8 shrink-0">
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Rename chat</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  {currentConversation && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={handlePrintChat}
                            className="h-8 w-8 shrink-0"
                          >
                            <Printer className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Print chat</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span>{formatMunicipalityLabel(municipality)}</span>
            </div>
          </div>
          <ScrollArea
            aria-label={`Chat messages - ${messageCount} messages`}
            role="log"
            aria-live="polite"
            aria-atomic="false"
            aria-relevant="additions"
          >
            <div className="mx-auto grid gap-2 lg:gap-4 p-1 md:p-2 lg:p-4">
              <BotWelcomeMessage />
              {renderInitialAddressDetails()}
              {prompts.length > 0 && currentConversation?.messages?.length === 0 && (
                <div className="p-2 space-y-1">
                  <p className="text-sm text-muted-foreground">Suggested questions:</p>
                  <div className="flex flex-wrap gap-2">
                    {[...prompts].sort(() => 0.5 - Math.random()).slice(0, 3).map((prompt, index) => (
                      <Button
                        key={index}
                        variant="secondary"
                        size="sm"
                        className="whitespace-normal break-words h-auto text-left text-sm text-muted-foreground hover:text-foreground"
                        onClick={() => {
                          const textarea = document.querySelector('textarea[name="prompt"]') as HTMLTextAreaElement;
                          if (textarea) {
                            textarea.value = prompt;
                            textarea.focus();
                          }
                        }}
                      >
                        {prompt}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
              {currentConversation?.messages?.map((message: ChatMessage, index: number) => {
                const messages = currentConversation?.messages || [];
                const isLastBotMessage = index === messages.length - 1 && message.source === "from_bot";
                const previousMessage = index > 0 ? messages[index - 1] : null;
                const researchSummary = isLastBotMessage ? lastResearchSummary : null;

                return message.source === "from_bot" ? (
                  <div key={message.id}>
                    <BotMessage
                      message={message}
                      aria-setsize={messageCount}
                      aria-posinset={index + 1}
                      researchSummary={researchSummary}
                      onAddressChatRedirect={handleAddressChatRedirect}
                      onRegisterOpenAnswerReasoning={
                        isLastBotMessage ? registerOpenAnswerReasoning : undefined
                      }
                    />
                    {isLastBotMessage && previousMessage && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {improvedPrompts.some(p => p.messageId === String(message.id)) && (
                          <div className="flex flex-wrap gap-2">
                            {improvedPrompts
                              .filter(p => p.messageId === String(message.id))
                              .map((improvedPrompt, i) => (
                                <Button
                                  key={i}
                                  variant="secondary"
                                  size="sm"
                                  onClick={() => handleSuggestedPrompt(improvedPrompt.prompt, improvedPrompt.messageId)}
                                  className="whitespace-normal break-words h-auto text-left text-sm"
                                >
                                  <FlaskConical className="h-4 w-4 mr-2" />
                                  {improvedPrompt.prompt}
                                </Button>
                              ))}
                          </div>
                        )}
                        {!improvedPrompts.some(p => p.messageId === String(message.id)) && previousMessage.source === "from_human" && (
                          <>
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => handleImprovePrompt(previousMessage.content, message.id)}
                              disabled={isImprovingPrompt}
                            >
                              {isImprovingPrompt ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              ) : (
                                <GraduationCap className="h-4 w-4 mr-2" />
                              )}
                              Improve My Prompt
                            </Button>
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => handleSuggestQueries(message.content, String(message.id))}
                              disabled={suggestingMessageId === String(message.id)}
                            >
                              {suggestingMessageId === String(message.id) ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              ) : (
                                <FlaskConical className="h-4 w-4 mr-2" />
                              )}
                              Suggest Related Questions
                            </Button>
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => handleSummarizeSources(message.meta_data.citation.map((c: Record<string, string>) => {
                                const [title, body] = Object.entries(c)[0];
                                return `📚 ${title}\n\n${body}`;
                              })
                                .join('\n\n---\n\n'), String(message.id))}
                              disabled={isSummarizingSources}
                            >
                              {isSummarizingSources ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              ) : (
                                <BookCheck className="h-4 w-4 mr-2" />
                              )}
                              Summarize Sources
                            </Button>
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => openTranslationDialog(message.id)}
                              disabled={isTranslating}
                            >
                              {isTranslating ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              ) : (
                                <Languages className="h-4 w-4 mr-2" />
                              )}
                              Translate This Response
                            </Button>
                            {countCitedSourcesWithBody(
                              message.content,
                              message.meta_data?.citation
                            ) > 0 && (
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => {
                                  void openAnswerReasoningForLastBotRef.current?.();
                                }}
                                aria-label={`See ${ANSWER_REASONING_LABEL}`}
                              >
                                <ScrollText className="h-4 w-4 mr-2" />
                                See {ANSWER_REASONING_LABEL}
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <UserMessage
                    key={message.id}
                    message={message}
                    aria-setsize={messageCount}
                    aria-posinset={index + 1}
                  />
                );
              })}

              {isStreamingResponse && isLastMessageHuman && (
                <>
                  <TaskListLoadingState
                    progressMessage={progressMessage}
                    isStreamingResponse={isStreamingResponse}
                    streamingStartTime={streamingStartTime}
                    onStreamingStartTimeUpdate={setStreamingStartTime}
                    researchSummaryRef={researchSummaryRef}
                  />
                  <span className="h-2" ref={ref} />
                </>
              )}
            </div>
          </ScrollArea>
          <div className="p-4">
            {currentConversation && municipality && (
              <div className="mb-2 px-3 py-2 flex justify-between items-center text-sm border border-border rounded-md">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <span>
                    {nextMessageDocumentLabel(selectedDocumentIds.length, totalDocumentCount)}
                  </span>
                </div>
                <DocumentSelector
                  chatId={currentConversation.id}
                  municipalityId={municipality.id}
                  selectedDocuments={selectedDocumentIds}
                  allowSaveAsDefault
                  promptForEmptySelection={promptForEmptySelection}
                  onSelectionChange={(newSelection, totalDocs) => {
                    const finalSelection = [...newSelection];

                    setSelectedDocumentIds(finalSelection);

                    // If we got the total document count, update it
                    if (totalDocs !== undefined) {
                      setTotalDocumentCount(totalDocs);
                    }

                    // Also update the conversation in context to ensure immediate sync
                    if (currentConversation) {
                      setCurrentConversationForKind("research_assistant", {
                        ...currentConversation,
                        configuration: {
                          ...currentConversation.configuration,
                          currently_selected_documents: finalSelection
                        }
                      });
                    }
                  }}
                  onDocumentCountLoaded={(count) => {
                    setTotalDocumentCount(count);
                  }}
                />
              </div>
            )}
            <Card className="focus-within:border-primary">
              <form className="flex gap-2 p-2" onSubmit={handleSendMessage}>
                <Textarea
                  id="prompt"
                  name="prompt"
                  placeholder="Type your message here..."
                  className="flex-1 min-h-[60px] resize-none border-none focus-visible:ring-0"
                  rows={3}
                  autoFocus
                  required
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey && !isListening) {
                      e.preventDefault();
                      e.currentTarget.form?.requestSubmit();
                    }
                  }}
                />
                <div className="flex flex-row gap-2 items-end">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          variant={isListening ? "default" : "outline"}
                          size="icon"
                          onClick={toggleSpeechRecognition}
                          disabled={isStreamingResponse}
                          className={`${isListening ? 'bg-red-500 hover:bg-red-600' : ''}`}
                          aria-label={isListening ? 'Stop dictation' : 'Start dictation'}
                        >
                          {isListening ? (
                            <MicOff className="h-4 w-4" />
                          ) : (
                            <Mic className="h-4 w-4" />
                          )}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{isListening ? 'Stop dictation' : 'Start dictation'}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <Button
                    className={`${isStreamingResponse ? 'cursor-wait' : ''}`}
                    type="submit"
                    disabled={isStreamingResponse || isListening}
                  >
                    {isStreamingResponse ? (
                      <span>Waiting<span className="animate-pulse">...</span></span>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Send
                      </>
                    )}
                  </Button>
                </div>
              </form>
              {humanMessageCount >= 5 && (
                <p className="text-xs text-muted-foreground px-3 pb-2 pt-0">
Research Assistant performs best with focused conversations. We suggest{" "}
                <button
                  type="button"
                  onClick={handleNewChat}
                  className="underline hover:text-foreground font-medium"
                >
                  starting a new chat
                </button>{" "}
                when topics change.
                </p>
              )}
            </Card>
            <AiDisclaimer className="text-center text-gray-500 pt-2" />
          </div>
        </div>
      </Sheet>

      <NewChatDialog
        open={showNewChatDialog}
        onOpenChange={handleNewChatDialogOpenChange}
        initialMunicipality={selectedMunicipality}
        onMunicipalityChange={setSelectedMunicipality}
        onStartChat={handleStartChat}
        municipalities={municipalities}
        title={addressRedirectLock?.title}
        description={addressRedirectLock?.description}
        lockedMunicipalityId={addressRedirectLock?.municipality_id}
      />
      <TranslationDialog
        open={showTranslationDialog}
        onOpenChange={setShowTranslationDialog}
        messageId={messageToTranslate}
        isTranslating={isTranslating}
        onTranslate={handleTranslateResponse}
      />
    </>
  );
}
